import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { catchError, finalize, tap } from "rxjs/operators";
import { of } from "rxjs";
import { ClientSettingsService } from "../../manageb/client-settings.service";
import { ClientSettings, ClientSettingsUpdateBody, CLIENT_SETTINGS_DEFAULTS, cloneClientSettings, isDischargePlanEnabled, isGatewayReferralLinksDisabled, isMdsRolesEnabled, isMessagesVisibleToAllFacilities, isScheduledEmailsEnabled } from "../../manageb/client-settings.types";

@Component({
  selector: 'client-settings',
  templateUrl: './client-settings.component.html',
  styleUrls: ['./client-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientSettingsComponent implements OnInit, OnDestroy {

  settings: ClientSettings = cloneClientSettings(CLIENT_SETTINGS_DEFAULTS);

  private savedSnapshot: string | null = null;
  private saveSuccessTimer: ReturnType<typeof setTimeout> | null = null;
  private saveErrorTimer: ReturnType<typeof setTimeout> | null = null;
  private saveNoChangesTimer: ReturnType<typeof setTimeout> | null = null;
  private saveConflictTimer: ReturnType<typeof setTimeout> | null = null;
  private serverMessagesVisible = false;

  loading = false;
  loadError = false;
  saving = false;
  saveSuccess = false;
  saveError = false;
  saveNoChanges = false;
  saveConflict = false;

  private readonly TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

  constructor(
    private cdr: ChangeDetectorRef,
    private clientSettingsService: ClientSettingsService,
  ) {}

  ngOnInit(): void {
    this.loadSettings();
  }

  ngOnDestroy(): void {
    if (this.saveSuccessTimer !== null) clearTimeout(this.saveSuccessTimer);
    if (this.saveErrorTimer !== null) clearTimeout(this.saveErrorTimer);
    if (this.saveNoChangesTimer !== null) clearTimeout(this.saveNoChangesTimer);
    if (this.saveConflictTimer !== null) clearTimeout(this.saveConflictTimer);
  }

  get canSave(): boolean {
    return !this.loading
      && !this.loadError
      && !this.saving
      && this.savedSnapshot !== null
      && this.isCensusHourValid;
  }

  get isCensusHourValid(): boolean {
    return this.settings.censusReportHour === ''
      || this.TIME_PATTERN.test(this.settings.censusReportHour);
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  save(): void {
    if (!this.canSave) return;

    const body = this.buildBody();
    const currentSnapshot = JSON.stringify(body);
    if (currentSnapshot === this.savedSnapshot) {
      this.flashNoChanges();
      return;
    }

    this.saving = true;
    this.saveSuccess = false;
    this.saveError = false;
    this.saveNoChanges = false;
    this.saveConflict = false;

    const sentMessagesVisible = body.uiSettings.messagesVisibleToAllFacilities === true;
    this.clientSettingsService.updateAdmin({
      ...body,
      expected: { uiSettings: { messagesVisibleToAllFacilities: this.serverMessagesVisible } },
    }).pipe(
      tap(() => {
        this.savedSnapshot = currentSnapshot;
        this.serverMessagesVisible = sentMessagesVisible;
        this.flashSuccess();
      }),
      catchError(error => {
        console.error('client-settings save failed', error?.status);
        const stored = error?.error?.uiSettings?.messagesVisibleToAllFacilities;
        if (error?.status === 409 && typeof stored === 'boolean') {
          this.applyStoredMessagesVisible(stored);
          this.flashConflict();
        } else {
          this.flashError();
        }
        return of(null);
      }),
      finalize(() => {
        this.saving = false;
        this.cdr.markForCheck();
      }),
    ).subscribe();
  }

  private applyStoredMessagesVisible(stored: boolean): void {
    this.serverMessagesVisible = stored;
    this.settings = {
      ...this.settings,
      uiSettings: { ...this.settings.uiSettings, messagesVisibleToAllFacilities: stored },
    };
    if (this.savedSnapshot === null) return;
    // A 409 writes nothing, so only the flag moved on the server; the other unsaved edits stay pending.
    const saved: ClientSettingsUpdateBody = JSON.parse(this.savedSnapshot);
    this.savedSnapshot = JSON.stringify({
      ...saved,
      uiSettings: { ...saved.uiSettings, messagesVisibleToAllFacilities: stored },
    });
  }

  private hasUnsavedChanges(): boolean {
    if (this.savedSnapshot === null) return false;
    return JSON.stringify(this.buildBody()) !== this.savedSnapshot;
  }

  private buildBody(): ClientSettingsUpdateBody {
    return {
      censusReportHour: this.settings.censusReportHour,
      showUnassignedFacilities: this.settings.showUnassignedFacilities,
      approvedAdmission: this.settings.approvedAdmission,
      showAiAdmissionOutlook: this.settings.showAiAdmissionOutlook,
      showMedicareEligibility: this.settings.showMedicareEligibility,
      useNewPatientInfo: this.settings.useNewPatientInfo,
      uiSettings: {
        newVersionPages: { ...this.settings.uiSettings.newVersionPages, jobInfo: this.settings.uiSettings.newVersionPages?.jobInfo ?? false, referralResponseReport: this.settings.uiSettings.newVersionPages?.referralResponseReport ?? false },
        ...(this.settings.uiSettings.products?.risk !== undefined
          ? { products: { risk: this.settings.uiSettings.products.risk } }
          : {}),
        // Always a boolean: the toggle shows a never-configured region as OFF
        // (see loadSettings), and saving that as an explicit false is the same
        // reading the server and every client-side reader already apply.
        dischargePlanEnabled: isDischargePlanEnabled(this.settings.uiSettings),
        scheduledEmailsEnabled: isScheduledEmailsEnabled(this.settings.uiSettings),
        mdsRolesEnabled: isMdsRolesEnabled(this.settings.uiSettings),
        messagesVisibleToAllFacilities: isMessagesVisibleToAllFacilities(this.settings.uiSettings),
        gatewayReferralLinksDisabled: isGatewayReferralLinksDisabled(this.settings.uiSettings),
        timezone: this.settings.uiSettings.timezone,
      },
    };
  }

  private flashSuccess(): void {
    this.saveSuccess = true;
    this.cdr.markForCheck();
    if (this.saveSuccessTimer !== null) clearTimeout(this.saveSuccessTimer);
    this.saveSuccessTimer = setTimeout(() => {
      this.saveSuccess = false;
      this.saveSuccessTimer = null;
      this.cdr.markForCheck();
    }, 3000);
  }

  private flashError(): void {
    this.saveError = true;
    this.cdr.markForCheck();
    if (this.saveErrorTimer !== null) clearTimeout(this.saveErrorTimer);
    this.saveErrorTimer = setTimeout(() => {
      this.saveError = false;
      this.saveErrorTimer = null;
      this.cdr.markForCheck();
    }, 5000);
  }

  private flashConflict(): void {
    this.saveConflict = true;
    this.cdr.markForCheck();
    if (this.saveConflictTimer !== null) clearTimeout(this.saveConflictTimer);
    this.saveConflictTimer = setTimeout(() => {
      this.saveConflict = false;
      this.saveConflictTimer = null;
      this.cdr.markForCheck();
    }, 8000);
  }

  private flashNoChanges(): void {
    this.saveNoChanges = true;
    this.cdr.markForCheck();
    if (this.saveNoChangesTimer !== null) clearTimeout(this.saveNoChangesTimer);
    this.saveNoChangesTimer = setTimeout(() => {
      this.saveNoChanges = false;
      this.saveNoChangesTimer = null;
      this.cdr.markForCheck();
    }, 3000);
  }

  private loadSettings(): void {
    this.loading = true;
    this.loadError = false;
    this.clientSettingsService.loadAdmin().pipe(
      tap(settings => {
        if (!settings) {
          this.loadError = true;
          return;
        }
        const loaded = cloneClientSettings(settings);
        // The form binds a checkbox to this key, and a checkbox has no third
        // state: a region that never configured the flag is OFF (opt-in), so
        // undefined is normalised to the same false the readers derive.
        this.settings = {
          ...loaded,
          uiSettings: {
            ...loaded.uiSettings,
            dischargePlanEnabled: isDischargePlanEnabled(loaded.uiSettings),
            scheduledEmailsEnabled: isScheduledEmailsEnabled(loaded.uiSettings),
            mdsRolesEnabled: isMdsRolesEnabled(loaded.uiSettings),
            messagesVisibleToAllFacilities: isMessagesVisibleToAllFacilities(loaded.uiSettings),
            gatewayReferralLinksDisabled: isGatewayReferralLinksDisabled(loaded.uiSettings),
          },
        };
        this.serverMessagesVisible = isMessagesVisibleToAllFacilities(loaded.uiSettings);
        this.savedSnapshot = JSON.stringify(this.buildBody());
      }),
      catchError(error => {
        console.error('client-settings load failed', error?.status);
        this.loadError = true;
        return of(null);
      }),
      finalize(() => {
        this.loading = false;
        this.cdr.markForCheck();
      }),
    ).subscribe();
  }
}
